import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts } from '../../components/LessonUI';

const modernSelectorsCode = `// 1. querySelector - Surgical precision for the first match
const nav = document.querySelector('nav#main-nav');
const firstActive = document.querySelector('.list-item.active');

// 2. querySelectorAll - Retrieving a STATIC NodeList
const allCards = document.querySelectorAll('.card');

// Efficiently iterating without array conversion
allCards.forEach(card => {
    card.classList.add('fade-in');
});

// Using complex CSS combinators
const nestedInput = document.querySelector('form &gt; fieldset input:checked');`;

const classicSelectorsCode = `// 1. getElementById - The Engine's "Fast Lane"
const portal = document.getElementById('user-portal'); 

// 2. getElementsByClassName - Returning a LIVE HTMLCollection
const widgets = document.getElementsByClassName('widget');

console.log(widgets.length); // 3
const newWidget = document.createElement('div');
newWidget.className = 'widget';
document.body.appendChild(newWidget);

// The collection automatically reflects the change!
console.log(widgets.length); // 4

// 3. getElementsByTagName - Global tag tracking
const scripts = document.getElementsByTagName('script');`;

const performanceComparisonCode = `// Benchmarking Query Performance (Approximate)

//    Fastest (Direct index lookup)
console.time('ID');
document.getElementById('save-btn');
console.timeEnd('ID');

//   Slowest (CSS Selector Engine overhead)
console.time('QuerySelector');
document.querySelector('#save-btn');
console.timeEnd('QuerySelector');

// Architectural Pro-Tip: Scoping
const container = document.getElementById('dashboard');
// Faster because querySelector only searches the 'dashboard' subtree
const status = container.querySelector('.status-indicator');`;

const collectionBehaviorCode = `// Live HTMLCollection vs. Static NodeList

const liveDots = document.getElementsByClassName('dot');
const staticDots = document.querySelectorAll('.dot');

// If we remove an element from the DOM:
document.querySelector('.dot').remove();

console.log(liveDots.length);   // Syncs with DOM (Decreases by 1)
console.log(staticDots.length); // Snapshot of history (Remains same)

// Logic hazard: Be careful when looping over LIVE collections 
// and modifying the DOM, as the index will shift mid-loop!`;

const robustnessPatternsCode = `// Defensive Programming in DOM Selection

// Use Optional Chaining to prevent "Cannot read property of null"
const sidebar = document.querySelector('.sidebar');
sidebar?.classList.toggle('collapsed');

// Checking NodeList length before processing
const items = document.querySelectorAll('.item');
if (items.length &gt; 0) {
    processItems(items);
}

// Data Attribute Selection (Recommended for decoupling logic from styles)
const deleteBtn = document.querySelector('[data-action="delete-user"]');`;

const JSDOMSelection = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">DOM Manipulation</div>
                <h1>Surgical Element Selection</h1>
                <p className="hero-lead">
                    Master the art of querying the document tree. Learn why
                    `getElementById` remains the performance king, how CSS
                    selectors introduce architectural overhead, and the
                    critical difference between live and static collections.
                </p>
            </div>

            <div className="content-section">
                <h2>The Selector Spectrum: From Speed to Flexibility</h2>
                <p>
                    Selecting an element is the fundamental operation that bridges your logic to the user interface.
                    In modern JavaScript development, we have two primary families of selectors: the "Classic"
                    methods (like `getElementById`) and the "Modern" methods (like `querySelector`). The Modern
                    methods are incredibly flexible, allowing you to use any valid CSS selector stringincluding
                    pseudo-classes like `:hover` or complex combinators like &gt;. This flexibility, however,
                    comes with a small performance cost as the browser must invoke its CSS parsing engine to
                    find the target. In contrast, the Classic methods are direct and highly optimized by the
                    browser's internal indexing system. For high-performance applications that perform
                    hundreds of queries per second, choosing the right method is a vital engineering
                    decision that impacts the overall responsiveness of the page.
                </p>
                <p>
                    Regardless of which method you choose, the resulting reference is a "live" pointer to
                    the element in the browser's memory. This means that if you store an element in a
                    variable and later modify its appearance via a different script, your variable will
                    reflect those changes immediately. It also means that you must be careful about memory
                    management; holding onto a reference to a DOM element that has been removed from the
                    page prevents it from being garbage collected, potentially creating a "Detached DOM"
                    memory leak. As a best practice, you should only cache the elements you intend to
                    interact with frequently and nullify those references if the associated UI component
                    is destroyed. Understanding the lifecycle of these selections is essential for
                    building stable, long-running single-page applications.
                </p>

                <CodeBlock code={modernSelectorsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>The Performance Hierarchy: Why IDs Still Matter</h2>
                <p>
                    While `querySelector` looks cleaner and is more versatile, `document.getElementById`
                    remains the undisputed performance champion of DOM selection. Internally, browser
                    engines maintain a specialized hash map of all element IDs, making retrieval
                    nearly O(1) in complexity. When you use `querySelector('#my-id')`, the browser
                    often has to treat it as a general CSS selector first, parsing the string and
                    checking types before eventually reaching the same ID optimization path. While
                    this overhead is measured in microseconds, it accumulates in large-scale applications
                    or during critical execution loops like physics simulations or high-frequency
                    data updates. For targeted, high-priority UI elements, the classic ID-based
                    lookup is still the gold standard for industrial-strength code.
                </p>
                <p>
                    Another powerful performance optimization is **Scoped Selection**. Instead of
                    always querying from the global `document`, you can call query methods on any
                    specific Element node. This tells the browser to only search the subtree of
                    that element, significantly reducing the number of nodes the engine has to
                    traverse. For example, if you have a massive table with 1,000 rows, searching
                    for an "active" class inside a single row is exponentially faster than
                    searching the entire document. Senior engineers architect their code to always
                    operate on the smallest possible subset of the DOM tree. This "Search Subtree"
                    pattern not only improves speed but also enhances modularity by ensuring
                    that components only "see" their own internal elements, preventing
                    accidental interactions with other parts of the page.
                </p>

                <CodeBlock code={classicSelectorsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Collections: The Live vs. Static Trap</h2>
                <p>
                    Perhaps the most subtle source of bugs in DOM manipulation is the distinction
                    between **HTMLCollections** and **NodeLists**. Methods like `getElementsByClassName`
                    return an `HTMLCollection`, which is "live." This means the collection is not a
                    snapshot, but a dynamically updated view of the DOM. If you add or remove an
                    element that matches the criteria, the collection's length and contents change
                    automatically without any further action. This can be problematic when looping
                    through a collection and removing elements; since the length decreases mid-loop,
                    traditional `for` loops will skip elements as the indices shift. To avoid this,
                    it is often safer to convert live collections into standard arrays using
                    `Array.from()` or the spread operator if you plan on mutating the DOM during iteration.
                </p>
                <p>
                    In contrast, `querySelectorAll` returns a `NodeList` that is "static" (in most
                    standard cases). This represents a snapshot of the DOM at the exact millisecond the
                    query was performed. If the DOM changes afterward, the `NodeList` remains untouched.
                    This static nature makes it much safer for complex logic and batch processing, as
                    you don't have to worry about the collection shrinking or growing under your feet.
                    Furthermore, NodeLists natively support the `.forEach()` method, allowing for cleaner
                    logic without the overhead of manual array conversion. Understanding which
                    selection method returns which type of collection is a fundamental requirement
                    for writing predictable asynchronous logic and reactive UI components that
                    need to track state over time.
                </p>

                <CodeBlock code={collectionBehaviorCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Defensive Selection & Modern Patterns</h2>
                <p>
                    In real-world applications, you should never assume a DOM selection will succeed.
                    Factors like asynchronous template rendering, network delays, or user permissions
                    can result in a query returning `null`. Attempting to access a property on `null`
                    (like `element.style`) will throw a runtime error that crashes your entire
                    script. The modern "safe" way to handle this is via **Optional Chaining**
                    (the `?.` operator). This allows the engine to gracefully short-circuit and return
                    `undefined` rather than crashing if the element is missing. Combined with
                    nullish coalescing for fallback values, you can build highly resilient
                    UI logic that handles "element not found" scenarios without cluttering your
                    code with repeated `if` checks.
                </p>
                <p>
                    Another powerful pattern is selecting by **Custom Data Attributes** (`[data-*]`).
                    Relying on CSS classes for JavaScript logic is a common architectural anti-pattern
                    because it couples your functionality to your styling. If a designer changes a
                    class name to update the UI, they unknowingly break the JavaScript. By using
                    data attributes specifically for selection (e.g., `[data-action="save"]`), you
                    provide a clear signal to other developers that this element is part of a
                    behavioral contract. This separation of concerns makes your codebase more
                    maintainable and prevents the "fragile selector" syndrome that plagues many
                    legacy jQuery-style applications. Modern enterprise front-ends treat the
                    data layer and the style layer as two distinct axes of the DOM.
                </p>

                <CodeBlock code={robustnessPatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Engineering Best Practices</h2>
                <p>
                    Avoid the "Universal Selector" (`*`) in performance-critical areas, as it
                    forces the browser to visit every single node in the tree. When building large
                    lists or tables, use event delegation (selecting the parent) rather than
                    selecting every individual child; this drastically reduces the number of
                    selection calls and event listeners in memory. Always cache your DOM
                    references at the top of your function scope if you need to use them more
                    than once. Finally, use the browser's DevTools "Performance" and "Memory"
                    profiles to identify if your selection logic is causing unnecessary layout
                    calculations or creating detached DOM leaks that slow down the user's
                    device over time.
                </p>

                <KeyFacts><ul>
                        <li>**Priority:** Use `getElementById` for unique, high-performance lookups.</li>
                        <li>**Flexibility:** Use `querySelector` for complex CSS-based logic.</li>
                        <li>**Scoping:** Call selection methods on specific elements to limit search depth.</li>
                        <li>**Safety:** Use Optional Chaining (`?.`) to handle missing nodes gracefully.</li>
                        <li>**Observation:** Understand if your query returns a LIVE or STATIC collection.</li>
                        <li>**Decoupling:** Prefer `[data-*]` attributes for logic to separate behavior from styles.</li>
                        <li>**Optimization:** Cache references instead of re-querying in loops or frequent events.</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default JSDOMSelection;


